import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import mlgLogo from "../assets/images/mlg-logo.png";
import { clearAuth, apiRequest } from "../api/apiClient";
import { settingsService } from "../services/api/settings.service";
import { HasModulePermission, ModuleId } from "../utils/permissions";
import { getFullPath } from "../utils/getFullPath";

interface TasksPendingPerRole {
  workflowStageId: number;
  workflowStageName: string;
  roleId: number;
  roleName: string;
  count: number;
}

interface NotificationsResponse {
  pendingTasks: number;
  tasksPendingPerRoles: TasksPendingPerRole[];
}

type TopbarProps = {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
};

const Topbar = ({ onMenuToggle, sidebarOpen }: TopbarProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [appName, setAppName] = useState("Traditional Marriage & Divorce");
  const [notifications, setNotifications] =
    useState<NotificationsResponse | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const firstName = localStorage.getItem("twdc_first_name") ?? "User";
  const roleName = localStorage.getItem("twdc_role_name") ?? "Role";
  const userImage = localStorage.getItem("twdc_user_image");
  const resolvedUserImage = getFullPath(userImage ?? undefined);
  const userInitial = firstName.trim().charAt(0).toUpperCase() || "U";
  const { title } = usePageTitle();

  const fetchNotifications = async () => {
    try {
      const response = await apiRequest("/Notifications", { method: "GET" });
      if (response.ok) {
        const data: NotificationsResponse = await response.json();
        setNotifications(data);
      }
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadAppName = async () => {
      try {
        const setting = await settingsService.getSettingByKey("AppName");
        if (setting?.value?.trim()) {
          setAppName(setting.value.trim());
          return;
        }

        const fallback = await settingsService.getSettingByKey("appName");
        if (fallback?.value?.trim()) {
          setAppName(fallback.value.trim());
        }
      } catch {
        // Keep default app name when settings are unavailable.
      }
    };

    loadAppName();
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (!notifRef.current?.contains(event.target as Node)) {
        setNotifOpen(false);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const handleLogout = () => {
    clearAuth();
    setMenuOpen(false);
    navigate("/");
  };

  return (
    <header className="topbar">
      <div className="topbar-left">
        <div className="brand">
          <div
            className="brand-mark"
            style={{ cursor: "pointer" }}
            onClick={() =>
              navigate(
                HasModulePermission(ModuleId.Dashboard)
                  ? "/dashboard"
                  : "/profile",
              )
            }
          >
            <img src={mlgLogo} alt="Ministry of Local Government" />
          </div>
          <div className="brand-text">
            <strong>{appName}</strong>
            <span>Admin Dashboard</span>
            <span className="brand-short">Marriage & Divorce Certificates</span>
          </div>
        </div>
        <button
          className={`menu-toggle ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          onClick={onMenuToggle}
          aria-expanded={sidebarOpen}
          aria-label="Toggle sidebar"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            {sidebarOpen ? (
              <path d="M14.5 6l-5.5 6 5.5 6M9 12h11" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
        <span className="topbar-page-title">{title}</span>
      </div>
      <div className="topbar-actions">
        <div className="notif-menu" ref={notifRef}>
          <button
            className="icon-btn"
            type="button"
            aria-label="Notifications"
            onClick={() => setNotifOpen((prev) => !prev)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 1 0-14 0v5l-2 2v1h18v-1l-2-2Z" />
            </svg>
            {notifications && notifications.pendingTasks > 0 && (
              <span className="notif-badge">{notifications.pendingTasks}</span>
            )}
          </button>
          {notifOpen && (
            <div className="notif-panel">
              <div className="notif-panel-header">
                <strong>Pending Tasks</strong>
                <span className="notif-count">
                  {notifications?.pendingTasks ?? 0}
                </span>
              </div>
              {!notifications ||
              notifications.tasksPendingPerRoles.length === 0 ? (
                <div className="notif-empty">No pending tasks</div>
              ) : (
                <ul className="notif-list">
                  {notifications.tasksPendingPerRoles.map((task) => (
                    <li
                      key={`${task.workflowStageId}-${task.roleId}`}
                      className="notif-item"
                    >
                      <span className="notif-stage">
                        {task.workflowStageName}
                      </span>
                      <span className="notif-role">{task.roleName}</span>
                      <span className="notif-task-count">{task.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="user-menu" ref={menuRef}>
          <button
            className="user-trigger"
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <div className="avatar">
              {resolvedUserImage ? (
                <img src={resolvedUserImage || undefined} alt={firstName} />
              ) : (
                <span>{userInitial}</span>
              )}
            </div>
            <div className="user-meta">
              <span className="user-name">{firstName}</span>
              <span className="user-role"> ({roleName})</span>
            </div>
            <span className="chevron" />
          </button>
          <div className={`menu-panel ${menuOpen ? "is-open" : ""}`}>
            <Link to="/profile">Profile</Link>
            <button type="button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
